import { useEffect, useState, useRef } from 'react';
import parse from 'html-react-parser';
import { Modal } from 'react-bootstrap';

import { User } from '../../classess/user/user';
import { GlobalFormats } from '../../classess/main/globalFormats';
import { PasswordChange } from '../../classess/user/passwordChange';

import { isUndNullEmp, getLanguageObj, getResponse, settings, styles, handleResponseMessageAsync } from '../../helpers/main';
import { ProfileService } from '../../services/profile/ProfileService';

const { icons, images, labels, messages } = settings;

const currentMethod = 'ProfilePasswordEdit';

export default function ProfilePasswordEdit({
    modalObj = {
        $: {},
        type: 0,
        auth: {},
        showModal: false,
        languageObj: GlobalFormats?.getClass(getLanguageObj({ subMethod: currentMethod })?.result),
        dtProfileId: '',
        dtProfileRowId: 0,
        user: User?.getClass({}, false),
        setUser: {},
    },
    setModalObj = {},
}) {
    //#region variables
    const isMount = useRef(false);
    const [process, setProcess] = useState({
        save: false,
        back: false,
    });
    const [errors, setErrors] = useState({
        password: '',
        newPassword: '',
        newPasswordConfirm: '',
    });

    const [passwordObj, setPasswordObj] = useState({ ...PasswordChange?.class({}, false), userId: modalObj?.user?.id, });
    const [showPassword, setShowPassword] = useState(false);

    const [dataFormLength] = useState(PasswordChange?.minMaxLength(false));

    const controls = {
        buttons: {
            back: {
                defaultClass: 'btn btn-sm btn-outline-success',
                id: 'btnModalBack',
            },
            save: {
                defaultClass: 'btn btn-sm btn-outline-primary mx-1',
                id: 'btnModalSave',
            },
            closeModal: {
                defaultClass: 'close btn-danger fas fa-window-close',
                id: 'btnModalClose',
            },
        },
        icons: {
            save: {
                id: 'iconModalSave',
                defaultClass: icons?.fasFa?.save,
            },
            back: {
                id: 'iconModalBack',
                defaultClass: icons?.fasFa?.arrowCircleLeft,
            },
        },
        inputs: {
            password: {
                id: 'inputModalPassword',
            },
            newPassword: {
                id: 'inputModalNewPassword',
            },
            newPasswordConfirm: {
                id: 'inputModalNewPasswordConfirm',
            },
        },
    };
    //#endregion variables

    //#region methods
    const editPassword = async ({
        e = undefined,
        subMethod = ''
    } = {}) => {
        const currentMethod = 'editPassword';
        let response = getResponse({ type: 0 });
        try {
            e?.preventDefault?.();

            //#region validate data
            setErrors({ password: '', newPassword: '', newPasswordConfirm: '', });

            response = await validateFormData({ subMethod: currentMethod, });
            if (!response?.isSuccess || response?.isWarning) return;
            //#endregion validate data

            setShowPassword(false);
            setProcess(prev => ({ ...prev, save: true }));

            //#region password (update)
            response = await ProfileService.passwordChange({
                auth: modalObj?.auth,
                passwordObj,
                languageId: modalObj?.languageObj?.id,
                subMethod: currentMethod,
            });

            if (!response?.isSuccess || response?.isWarning) return;
            //#endregion password (update)

            response.msgText = `${messages?.processCompletedSuccessfully?.[modalObj?.languageObj?.id]}<br/>`;
            response.msgText += `${messages?.yourPasswordHasBeenChanged?.[modalObj?.languageObj?.id]}<br/>`;
            response.msgText += `${messages?.youCanNowLogInWithTheNewPassword?.[modalObj?.languageObj?.id]}.`;
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        } finally {
            if (!isMount?.current) return;

            setProcess(prev => ({ ...prev, save: false }));

            if (response?.isSuccess && !response?.isWarning) setDefaultValue({ closeModal: true, subMethod: currentMethod });

            if (!response?.isSuccess || response?.isWarning || response?.msgText !== '' || response?.msgTextHtml !== '') {
                await handleResponseMessageAsync({
                    response,
                    currentMethod,
                    auth: modalObj?.auth,
                    languageId: modalObj?.languageObj?.id
                });
            }
        }
    }

    const setDefaultValue = ({
        closeModal = false,
        subMethod = ''
    } = {}) => {
        const currentMethod = 'setDefaultValue';
        let response = getResponse({ type: 0 });
        try {
            if (closeModal) {
                setModalObj({
                    ...modalObj,
                    showModal: false,
                    type: 0,
                })
            }
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
            console.error(response?.msgText);
        }
    }

    const validateFormData = async ({
        subMethod = ''
    } = {}) => {
        const currentMethod = 'validateFormData';
        let response = getResponse({ type: 0 });
        try {
            const thisFieldRequired = `${messages?.thisFieldRequired?.[modalObj?.languageObj?.id]}.`;
            let localErrors = { password: '', newPassword: '', newPasswordConfirm: '', };
            let hasErrors = false;

            if (isUndNullEmp({ data: passwordObj?.password, subMethod: currentMethod })?.result) {
                localErrors.password = thisFieldRequired;
                hasErrors = true;
            }

            if (isUndNullEmp({ data: passwordObj?.newPassword, subMethod: currentMethod })?.result) {
                localErrors.newPassword = thisFieldRequired;
                hasErrors = true;
            }

            if (isUndNullEmp({ data: passwordObj?.newPasswordConfirm, subMethod: currentMethod })?.result) {
                localErrors.newPasswordConfirm = thisFieldRequired;
                hasErrors = true;
            }

            if (!hasErrors && passwordObj?.newPassword !== passwordObj?.newPasswordConfirm) {
                localErrors.newPassword = `${messages?.newPasswordAndNewPasswordConfirmNotMatch?.[modalObj?.languageObj?.id]}.`;
                localErrors.newPasswordConfirm = `${messages?.newPasswordAndNewPasswordConfirmNotMatch?.[modalObj?.languageObj?.id]}.`;
                hasErrors = true;
            }

            if (hasErrors) {
                setErrors(localErrors);
                response.isWarning = true;
            }
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    }

    useEffect(() => {
        isMount.current = true;
        return () => {
            isMount.current = false;
        };
    }, []);
    //#endregion methods

    return (
        <Modal backdrop='static' show={modalObj?.showModal} centered>
            <Modal.Header className='border-bottom-0 pt-4 px-4 bg-white'>
                <Modal.Title className='d-flex align-items-center w-100'>
                    <div className='bg-light p-2 rounded-circle mr-3 shadow-sm'
                        style={{ width: '45px', height: '45px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img alt='password' src={images?.icons?.password} style={{ width: '25px', height: '25px', objectFit: 'contain' }}></img>
                    </div>
                    <div className='d-flex flex-column'>
                        <span className='h5 mb-0 font-weight-bold text-dark'>{labels?.changePassword?.[modalObj?.languageObj?.id]}</span>
                        <div className='d-flex align-items-center mt-1'>
                            <span className={`badge badge-pill py-1 px-2 text-uppercase badge-warning text-dark`}
                                style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>
                                <i className={`${icons?.fasFa?.key} mr-1`}></i> {labels?.security?.[modalObj?.languageObj?.id] || 'Security'}
                            </span>
                        </div>
                    </div>
                </Modal.Title>
                <button id={controls?.buttons?.closeModal?.id}
                    type='button'
                    className='close border-0 bg-transparent'
                    disabled={process?.save}
                    style={{ fontSize: '1.5rem', marginTop: '-15px', color: '#6c757d', opacity: '0.5' }}
                    onClick={() => setDefaultValue({ closeModal: true })}>
                    <span aria-hidden='true'>&times;</span>
                </button>
            </Modal.Header>

            <form onSubmit={(e) => editPassword({ e: e })}>
                <Modal.Body className='p-0'>

                    <div className='card-body p-4'>
                        {modalObj?.type !== 2 ? (
                            <div className='row'>
                                <div className='col-md-12 mb-4'>
                                    <small className='text-muted text-uppercase font-weight-bold d-block '>
                                        <i className={icons?.fasFa?.lock} /> {labels?.password?.[modalObj?.languageObj?.id]}
                                    </small>
                                    <div className='input-group'>
                                        <input id={controls?.inputs?.password?.id}
                                            className='form-control bg-light border-0 shadow-sm text-dark'
                                            autoComplete='current-password'
                                            style={{ borderRadius: '5px' }}
                                            maxLength={dataFormLength?.max?.password}
                                            minLength={dataFormLength?.min?.password}
                                            onChange={(e) => setPasswordObj({ ...passwordObj, password: e?.target?.value, })}
                                            onFocus={() => setErrors(prev => ({ ...prev, password: '' }))}
                                            value={passwordObj?.password || ''}
                                            type={showPassword ? 'text' : 'password'}
                                            disabled={process?.save}
                                            placeholder={labels?.password?.[modalObj?.languageObj?.id]}
                                            title={labels?.password?.[modalObj?.languageObj?.id]} />
                                        <div className='input-group-append'>
                                            <button className='btn btn-light border-0 shadow-sm'
                                                style={{ borderRadius: '0 5px 5px 0' }}
                                                type='button'
                                                onClick={() => {
                                                    if (process?.save) return;
                                                    setShowPassword(!showPassword)
                                                }}>
                                                <i className={showPassword ? icons?.fasFa?.eye : icons?.fasFa?.eyeSlash} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className='form-text' style={styles?.inputMsgAlert}>{parse(errors?.password)}</div>
                                </div>

                                <div className='col-md-12 mb-4'>
                                    <small className='text-muted text-uppercase font-weight-bold d-block '>
                                        <i className={icons?.fasFa?.shieldAlt} /> {labels?.newPassword?.[modalObj?.languageObj?.id]}
                                    </small>
                                    <div className='input-group'>
                                        <input id={controls?.inputs?.newPassword?.id}
                                            className='form-control bg-light border-0 shadow-sm text-dark'
                                            autoComplete='new-password'
                                            style={{ borderRadius: '5px' }}
                                            maxLength={dataFormLength?.max?.password}
                                            minLength={dataFormLength?.min?.password}
                                            onChange={(e) => setPasswordObj({ ...passwordObj, newPassword: e?.target?.value, })}
                                            onFocus={() => setErrors(prev => ({ ...prev, newPassword: '' }))}
                                            value={passwordObj?.newPassword || ''}
                                            type={showPassword ? 'text' : 'password'}
                                            disabled={process?.save}
                                            placeholder={labels?.newPassword?.[modalObj?.languageObj?.id]}
                                            title={labels?.newPassword?.[modalObj?.languageObj?.id]} />
                                        <div className='input-group-append'>
                                            <button className='btn btn-light border-0 shadow-sm'
                                                style={{ borderRadius: '0 5px 5px 0' }}
                                                type='button'
                                                onClick={() => {
                                                    if (process?.save) return;
                                                    setShowPassword(!showPassword)
                                                }}>
                                                <i className={showPassword ? icons?.fasFa?.eye : icons?.fasFa?.eyeSlash} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className='form-text' style={styles?.inputMsgAlert}>{parse(errors?.newPassword)}</div>
                                </div>

                                <div className='col-md-12 mb-4'>
                                    <small className='text-muted text-uppercase font-weight-bold d-block '>
                                        <i className={icons?.fasFa?.checkCircle} /> {labels?.confirmNewPassword?.[modalObj?.languageObj?.id]}
                                    </small>
                                    <div className='input-group'>
                                        <input id={controls?.inputs?.newPasswordConfirm?.id}
                                            className='form-control bg-light border-0 shadow-sm text-dark'
                                            autoComplete='new-password'
                                            style={{ borderRadius: '5px' }}
                                            maxLength={dataFormLength?.max?.password}
                                            minLength={dataFormLength?.min?.password}
                                            onChange={(e) => setPasswordObj({ ...passwordObj, newPasswordConfirm: e?.target?.value, })}
                                            onFocus={() => setErrors(prev => ({ ...prev, newPasswordConfirm: '' }))}
                                            value={passwordObj?.newPasswordConfirm || ''}
                                            type={showPassword ? 'text' : 'password'}
                                            disabled={process?.save}
                                            placeholder={labels?.confirmNewPassword?.[modalObj?.languageObj?.id]}
                                            title={labels?.confirmNewPassword?.[modalObj?.languageObj?.id]} />
                                        <div className='input-group-append'>
                                            <button className='btn btn-light border-0 shadow-sm'
                                                style={{ borderRadius: '0 5px 5px 0' }}
                                                type='button'
                                                onClick={() => {
                                                    if (process?.save) return;
                                                    setShowPassword(!showPassword)
                                                }}>
                                                <i className={showPassword ? icons?.fasFa?.eye : icons?.fasFa?.eyeSlash} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className='form-text' style={styles?.inputMsgAlert}>{parse(errors?.newPasswordConfirm)}</div>
                                </div>
                            </div>
                        ) : (
                            <div className='text-muted text-center py-4'>
                                <i className={`${icons?.fasFa?.exclamationTrinagle} fa-3x mb-3 text-warning`} />
                                <p>{messages?.noDataFound?.[modalObj?.languageObj?.id]}</p>
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer className='border-top-0 pb-4 px-4 bg-white'>
                    <div className='d-flex justify-content-center w-100'>
                        <button id={controls?.buttons?.save?.id}
                            className='btn btn-primary shadow-sm font-weight-bold mx-1 px-4'
                            style={{ borderRadius: '5px' }}
                            disabled={process?.save}
                            hidden={modalObj?.type === 2}
                            tooltip='tooltip'
                            title={labels?.update?.[modalObj?.languageObj?.id]}
                            type='submit'>
                            {process?.save ? (
                                <span className={settings?.react?.html?.spinner?.borderSm} role='status' aria-hidden='true'></span>
                            ) : (
                                <i id={controls?.icons?.save?.id} className={controls?.icons?.save?.defaultClass} />
                            )} {labels?.update?.[modalObj?.languageObj?.id]}
                        </button>
                        <button id={controls?.buttons?.back?.id}
                            className='btn btn-outline-secondary shadow-sm font-weight-bold mx-1 px-4'
                            style={{ borderRadius: '5px' }}
                            disabled={process?.save}
                            tooltip='tooltip'
                            title={labels?.close?.[modalObj?.languageObj?.id]}
                            type='button'
                            onClick={() => setDefaultValue({ closeModal: true, subMethod: controls?.buttons?.back?.id })}>
                            <i id={controls?.icons?.back?.id} className={controls?.icons?.back?.defaultClass}></i> {labels?.back?.[modalObj?.languageObj?.id]}
                        </button>
                    </div>
                </Modal.Footer>
            </form>
        </Modal>
    )
}